import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';

import type { TPoint, TVertex, RegionPolygonProps } from '@vlabs/monokit/shared/types/regions';
import { PolygonShape } from '@vlabs/monokit/shared/ui/canvas-items';

// счётчик v_0, v_1, … для стабильных id вершин
const useIdGen = () => {
  const ref = useRef<number>(0);
  return useCallback(() => {
    const id = `v_${ref.current}`;
    ref.current += 1;
    return id;
  }, []);
};

// конвертеры между {x,y} и {x,y,id}
function toVertices(pts: TPoint[] | undefined, nextId: () => string): TVertex[] {
  return (pts ?? []).map((p) => ({ ...p, id: nextId() }));
}
function vtxToPts(vs: readonly TVertex[]): TPoint[] {
  return vs.map(({ x, y }) => ({ x, y }));
}

export const RegionPolygon: React.FC<RegionPolygonProps> = ({
  points,
  selected,
  drawing,
  TSizeBounds,
  color,
  filled,
  label,
  onChange,
  onSelect,
}) => {
  const makeId = useIdGen();
  const [vertices, setVertices] = useState<TVertex[]>(() => toVertices(points, makeId));
  const canEdit = selected && points.length >= 3 && !drawing;

  // синхронизация внешних KPoint[] → внутренних Vertex[]
  useEffect(() => {
    setVertices((prev) => {
      if (prev.length !== points.length) {
        // длина изменилась — перестроим с новыми id
        return toVertices(points, makeId);
      }
      // длина та же — обновим только координаты, сохраним id (меньше лишних ремоунтов)
      return prev.map((v, i) => ({ ...v, x: points[i].x, y: points[i].y }));
    });
  }, [points, makeId]);

  // handleMoveVertex / handleRemoveVertex / handleInsertAtEdge / handleTranslate — редактирование/перенос фигуры, всё через onChange
  const handleMoveVertex = (idx: number, p: TPoint) => {
    if (!canEdit) return;
    setVertices((arr) => {
      const next = [...arr];
      next[idx] = { ...next[idx], ...p };
      if (onChange) onChange(vtxToPts(next));
      return next;
    });
  };

  const handleRemoveVertex = (idx: number) => {
    if (!canEdit) return;
    setVertices((arr) => {
      const next = [...arr.slice(0, idx), ...arr.slice(idx + 1)];
      if (onChange) onChange(vtxToPts(next));
      return next;
    });
  };

  const handleInsertAtEdge = (idx: number, p: TPoint) => {
    if (!canEdit) return;
    setVertices((arr) => {
      const i2 = idx + 1;
      const next = [...arr.slice(0, i2), { ...p, id: makeId() }, ...arr.slice(i2)];
      if (onChange) onChange(vtxToPts(next));
      return next;
    });
  };

  const handleTranslate = (dx: number, dy: number) => {
    if (!canEdit) return;
    setVertices((arr) => {
      const next = arr.map((v) => ({ ...v, x: v.x + dx, y: v.y + dy }));
      if (onChange) onChange(vtxToPts(next));
      return next;
    });
  };

  return (
    <PolygonShape
      canDrag={canEdit}
      color={color}
      filled={filled}
      interactive={canEdit} // редактирование/drag — только когда можно
      label={label}
      onInsertAtEdge={handleInsertAtEdge}
      onMoveVertex={handleMoveVertex}
      onRemoveVertex={handleRemoveVertex}
      onSelect={onSelect} // клик по фигуре для выбора
      onTranslate={handleTranslate}
      points={vertices}
      selected={selected}
      TSizeBounds={TSizeBounds}
    />
  );
};
